import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApprovalsDetailDialog({ open, onOpenChange }: Props) {
  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ["dashboard-approvals-detail"],
    queryFn: async () => {
      const { data } = await supabase
        .from("approvals")
        .select("id, type, status, created_at, requested_by")
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: open,
  });

  const handleExport = () => {
    const headers = ["Type", "Status", "Requested Date"];
    const rows = approvals.map((a) => [a.type.replace(/_/g, " "), a.status, format(new Date(a.created_at), "yyyy-MM-dd")]);
    exportToCsv("pending-approvals.csv", headers, rows);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <DialogTitle>Pending Approvals</DialogTitle>
            <DialogDescription>Items awaiting your review</DialogDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={approvals.length === 0} className="mr-8 shrink-0">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
        ) : approvals.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No pending approvals</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {approvals.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="min-w-0">
                  <p className="text-sm font-medium capitalize truncate">{a.type.replace(/_/g, " ")}</p>
                  <p className="text-xs text-muted-foreground">Requested {format(new Date(a.created_at), "dd MMM yyyy")}</p>
                </div>
                <Badge variant="secondary">Pending</Badge>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
